// Copyright 2022 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import React, { useMemo, useState } from "react";
import { ErrorState } from "@constants/error";
import ErrorAlert from "@components/general/ErrorAlert";
import { useTranslation } from "react-i18next";
import { getLegacyFlag } from "@utils/toolbarItemUtils";
import PresessionErrorModal from "@components/browser/PresessionErrorModal";
import { getRebrandedTranslation } from "@utils/i18nUtils";
import { useAsyncEffect } from "use-async-effect";
import { useGetStreamingUrl } from "./useGetStreamingUrl";
import { useGetCookie } from "@hooks/useGetCookie";
import { shouldHideExtensionModal } from "@utils/cookieSyncUtils";
import { ExtensionModal } from "@components/presession/ExtensionModal";
import { useToolbarCookieResultNotification } from "@hooks/useToolbarCookieResultNotification";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

interface Props {
    onBrowserCreation: (endpoint: string) => void;
    onRedirectToLogin: (redirectToLogin: boolean) => void;
}

const BrowserFactory = ({ onBrowserCreation, onRedirectToLogin }: Props) => {
    const getStreamingUrl = useGetStreamingUrl();

    const { t } = useTranslation();
    const log = useGetLogger();
    const isLegacy = useMemo(() => getLegacyFlag(), []);

    const [errorState, setErrorState] = useState({
        errorState: ErrorState.NONE,
        errorHeader: "lease.errorAlert.serverError.header",
        errorMessage: "lease.errorAlert.serverError.message",
        requestId: undefined,
    });

    const onBrowserCreationError = (
        err: any,
        metricName: MetricName,
        requestId: string
    ) => {
        const name = err?.name;
        const message = err?.message;
        log.publishHttpStatusMetric(metricName, err?.$metadata?.httpStatusCode);
        switch (name) {
            case "ResourceNotReadyException":
                setErrorState({
                    errorState: ErrorState.INFO,
                    errorHeader: "lease.errorAlert.pendingPortal.header",
                    errorMessage: "lease.errorAlert.pendingPortal.message",
                    requestId,
                });
                break;
            case "AccessDeniedException":
                if (
                    message?.includes(
                        "Cannot access resource because account is deleted."
                    )
                ) {
                    setErrorState({
                        errorState: ErrorState.INFO,
                        errorHeader: "lease.errorAlert.suspendedPortal.header",
                        errorMessage:
                            "lease.errorAlert.suspendedPortal.message",
                        requestId,
                    });
                } else {
                    setErrorState({
                        errorState: ErrorState.ERROR,
                        errorHeader: errorState.errorHeader,
                        errorMessage: errorState.errorMessage,
                        requestId,
                    });
                }
                break;
            case "InternalServerException":
                if (message?.includes("V750620258")) {
                    setErrorState({
                        errorState: ErrorState.WARNING,
                        errorHeader:
                            "lease.errorAlert.userAccessLoggingBug.header",
                        errorMessage:
                            "lease.errorAlert.userAccessLoggingBug.message",
                        requestId,
                    });
                } else {
                    setErrorState({
                        errorState: ErrorState.ERROR,
                        errorHeader: errorState.errorHeader,
                        errorMessage: errorState.errorMessage,
                        requestId,
                    });
                }
                break;
            default:
                setErrorState({
                    errorState: ErrorState.ERROR,
                    errorHeader: errorState.errorHeader,
                    errorMessage: errorState.errorMessage,
                    requestId,
                });
        }
    };

    const { cookieResult, setCookieResult } = useGetCookie();
    useToolbarCookieResultNotification(cookieResult);

    const showExtensionModal = useMemo(
        () =>
            cookieResult?.error === "EXTENSION_NOT_INSTALLED" &&
            !shouldHideExtensionModal(),
        [cookieResult?.error]
    );

    /**
     * Trigger the streaming URL fetch and AS2 Embed
     * initialization.
     *
     * The Session cache is first checked for valid cached StreamingURLs. If
     * there are no valid streaming URLs, we get the latest cookies if they haven't
     * been fetched yet.
     *
     * Since CookieResult is a useState param, when it gets updated, the element
     * is re-rendered, calling the backend to get a new streaming URL.
     *
     * Irrespective of if we have the streaming URL cached or not, we do a
     * credential fetch before we reach this flow.
     *
     * TODO
     *      [LOWA-30289] Ideally, getting a streaming URL should be a custom hook.
     *      We will do this after refactoring alerts and modals
     */
    useAsyncEffect(async () => {
        if (cookieResult == null) {
            // Need to fetch cookie first
            return;
        }
        if (showExtensionModal) {
            // Need to show extension modal before starting a streaming session
            return;
        }
        try {
            const streamingUrl = await getStreamingUrl(cookieResult.cookies);
            onBrowserCreation(streamingUrl);
        } catch (err) {
            err &&
                onBrowserCreationError(
                    err,
                    MetricName.CREATE_REMOTE_BROWSER,
                    err.$metadata?.requestId
                );
        }
    }, [cookieResult, showExtensionModal]);

    if (showExtensionModal) {
        return (
            <ExtensionModal
                dismissExtensionModal={() => {
                    setCookieResult({
                        cookies: [],
                        error: null,
                    });
                }}
                onRedirectToLogin={onRedirectToLogin}
            />
        );
    }

    if (errorState.errorState === ErrorState.NONE) {
        // Once streaming URL is ready, main `App` component will be re-rendered.
        // TODO [LOWA-30289] use react-router or re-structure the components to avoid re-rendering
        return <></>;
    }

    if (isLegacy) {
        return (
            <div className="awsui On-top">
                <ErrorAlert
                    type={errorState.errorState}
                    header={getRebrandedTranslation(errorState.errorHeader, t)}
                    message={getRebrandedTranslation(
                        errorState.errorMessage,
                        t
                    )}
                    refreshButtonText={t("refresh.button.label")}
                    onRefreshButtonClick={() => window.location.reload()}
                    onDismissErrorAlert={() => {}}
                />
            </div>
        );
    }

    return (
        <PresessionErrorModal
            title={getRebrandedTranslation(errorState.errorHeader, t)}
            message={getRebrandedTranslation(errorState.errorMessage, t)}
            requestId={errorState.requestId}
            onDismiss={() => onRedirectToLogin(true)}
        />
    );
};

export default BrowserFactory;
